import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { useSession } from "@amber/access/ui";
import { roleAtLeast } from "@amber/contracts";
import { EDITOR_IDENTITY_REASON, EDITOR_PROFILES_REASON } from "@amber/profiles/contracts";
import type { RosterResponse, RosterRow } from "@amber/application/contracts";
import { ExportDialog, ImportPanel } from "@amber/profiles/ui";
import {
  ActiveFilters,
  Button,
  ColumnsMenu,
  DataTable,
  EmptyState,
  FiltersButton,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  TextInput,
  useColumnVisibility,
  useNarrow,
  type ColumnToggle,
} from "@amber/ui";
import { rosterQuery } from "./queries";
import { NARROW_HIDDEN, ROSTER_VISIBILITY, nameColumn, rosterColumns, rosterWidth } from "./RosterColumns";
import { facetOptions, filterRoster, rosterChips, toggled, type RosterChip } from "./rosterFilters";
import { RosterSidebar } from "./RosterSidebar";
import type { ProfilesSearch } from "./search";

function Figure({ n, children, muted }: { n: number; children: ReactNode; muted?: boolean }) {
  return (
    <span className={muted ? "text-muted" : undefined}>
      <b className="font-mono text-base">{n}</b> {children}
    </span>
  );
}

function RosterCounts({
  counts,
  summary,
}: {
  counts: ReadonlyMap<string, number>;
  summary: RosterResponse["summary"] | undefined;
}) {
  const reviews = summary?.plan_reviews_due ?? 0;
  return (
    <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-divider px-4 py-2.5 text-ui">
      <h1 id="view-title" className="sr-only">
        Profiles
      </h1>
      <Figure n={counts.get("tracked") ?? 0}>tracked</Figure>
      <Figure n={counts.get("paused") ?? 0}>paused</Figure>
      <Figure n={counts.get("draft") ?? 0}>draft</Figure>
      <Figure n={counts.get("pending") ?? 0}>with changes pending</Figure>
      <Figure n={summary?.open_gaps ?? 0}>open gaps</Figure>
      <Figure n={counts.get("not_tracked") ?? 0} muted>
        not tracked
      </Figure>
      <Figure n={reviews} muted>
        plan review{reviews === 1 ? "" : "s"} due in the next 30 days
      </Figure>
    </div>
  );
}

interface ToolbarProps {
  filtersOpen: boolean;
  onFilters: () => void;
  shown: number;
  chips: readonly RosterChip[];
  onChip: (chip: RosterChip) => void;
  onReset: () => void;
  text: string;
  setText: (next: string) => void;
  showNotTracked: boolean;
  setShowNotTracked: (next: boolean) => void;
  columns: readonly ColumnToggle[];
  editor: boolean;
  onPanel: (panel: "import" | "export") => void;
  onNew: () => void;
}

function ImportExportMenu({ editor, onPanel }: Pick<ToolbarProps, "editor" | "onPanel">) {
  return (
    <MenuRoot>
      <MenuTrigger asChild>
        <Button size="sm">Import / export</Button>
      </MenuTrigger>
      <MenuContent>
        <MenuItem
          disabled={!editor}
          title={editor ? undefined : EDITOR_IDENTITY_REASON}
          onSelect={() => {
            onPanel("import");
          }}
        >
          Import profiles…
        </MenuItem>
        <MenuItem
          disabled={!editor}
          title={editor ? undefined : EDITOR_IDENTITY_REASON}
          onSelect={() => {
            onPanel("export");
          }}
        >
          Export roster (CSV)…
        </MenuItem>
      </MenuContent>
    </MenuRoot>
  );
}

function RosterToolbar(props: ToolbarProps) {
  const { text, setText, showNotTracked, setShowNotTracked, editor } = props;
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
      <FiltersButton open={props.filtersOpen} onToggle={props.onFilters} />
      <span className="text-ui text-muted">{props.shown} profiles</span>
      <ActiveFilters
        filters={props.chips.map((c) => ({
          key: `${c.facet}-${c.key}`,
          label: c.label,
          value: c.value,
          onRemove: () => {
            props.onChip(c);
          },
        }))}
        onReset={props.onReset}
      />
      <span className="flex-1" />
      <label className="sr-only" htmlFor="roster-search">
        Name, alias, account or ID
      </label>
      <span className="w-[230px] flex-none">
        <TextInput
          id="roster-search"
          type="search"
          className="h-[30px]"
          placeholder="Name, alias, account or ID"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
        />
      </span>
      <label className="inline-flex h-[30px] items-center gap-2 rounded-lg border border-border px-2.5 text-ui">
        <input
          type="checkbox"
          role="switch"
          className="accent-[var(--color-link)]"
          checked={showNotTracked}
          onChange={(e) => {
            setShowNotTracked(e.target.checked);
          }}
        />
        Show not tracked
      </label>
      <ColumnsMenu columns={props.columns} />
      <ImportExportMenu editor={editor} onPanel={props.onPanel} />
      <Button variant="primary" size="sm" disabledReason={editor ? null : EDITOR_PROFILES_REASON} onClick={props.onNew}>
        New profile
      </Button>
    </div>
  );
}

function HiddenRow({ span, children }: { span: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={span} className="px-3 py-2 text-md text-muted">
        {children}
      </td>
    </tr>
  );
}

interface GridProps {
  pending: boolean;
  loaded: boolean;
  q: string;
  rows: { monitored: RosterRow[]; notTracked: RosterRow[]; retired: RosterRow[] };
  hidden: ReadonlySet<string>;
  hiddenNotTracked: number;
  hiddenRetired: number;
  onOpen: (id: string) => void;
  onReset: () => void;
  onShowNotTracked: () => void;
  onShowRetired: () => void;
}

function GridFooter(props: GridProps & { span: number; empty: boolean }) {
  const { span, hiddenNotTracked, hiddenRetired } = props;
  return (
    <>
      {props.loaded && props.empty ? (
        <tr>
          <td colSpan={span} className="px-3 py-6 text-center text-md text-muted">
            No profiles match these filters.{" "}
            <button type="button" className="font-strong text-link hover:underline" onClick={props.onReset}>
              Reset
            </button>
          </td>
        </tr>
      ) : null}
      {hiddenNotTracked > 0 ? (
        <HiddenRow span={span}>
          {hiddenNotTracked} not tracked {hiddenNotTracked === 1 ? "person" : "people"} hidden.{" "}
          <button type="button" className="font-strong text-link hover:underline" onClick={props.onShowNotTracked}>
            Show not tracked
          </button>
        </HiddenRow>
      ) : null}
      {hiddenRetired > 0 ? (
        <HiddenRow span={span}>
          {hiddenRetired} retired hidden.{" "}
          <button type="button" className="font-strong text-link hover:underline" onClick={props.onShowRetired}>
            Show retired
          </button>
        </HiddenRow>
      ) : null}
    </>
  );
}

function RosterGrid(props: GridProps) {
  const { monitored, notTracked, retired } = props.rows;
  const all = useMemo(() => [...monitored, ...notTracked, ...retired], [monitored, notTracked, retired]);
  const mutedIds = useMemo(() => new Set([...notTracked, ...retired].map((p) => p.id)), [notTracked, retired]);
  const [now] = useState(() => Date.now());
  const columns = useMemo(() => [nameColumn(props.q, mutedIds), ...rosterColumns(now)], [props.q, mutedIds, now]);
  const span = columns.length - props.hidden.size;
  const firstNotTracked = notTracked[0]?.id;
  return (
    <DataTable
      label="People"
      columns={columns}
      rows={all}
      rowId={(p) => p.id}
      minWidth={rosterWidth(props.hidden)}
      pinned={["name"]}
      hidden={props.hidden}
      isPending={props.pending}
      virtual={false}
      rowOptions={(p) => ({
        label: `${p.name}, ${p.state_label}`,
        ...(mutedIds.has(p.id) ? { className: "text-muted" } : {}),
        ...(p.id === firstNotTracked
          ? {
              before: (
                <tr>
                  <td colSpan={span} className="border-b border-divider bg-surface-2 px-3 py-1.5 text-md text-muted">
                    Identified speakers with a profile. Not monitored, and that is fine.
                  </td>
                </tr>
              ),
            }
          : {}),
        onOpen: () => {
          props.onOpen(p.id);
        },
      })}
      bodyAfter={<GridFooter {...props} span={span} empty={all.length === 0} />}
    />
  );
}

function RosterEmpty({ editor, onNew, onImport }: { editor: boolean; onNew: () => void; onImport: () => void }) {
  return (
    <EmptyState
      icon={<Users />}
      title="No profiles yet"
      actions={
        <>
          <Button variant="primary" size="sm" disabledReason={editor ? null : EDITOR_PROFILES_REASON} onClick={onNew}>
            New profile
          </Button>
          <Button size="sm" disabledReason={editor ? null : EDITOR_IDENTITY_REASON} onClick={onImport}>
            Import profiles…
          </Button>
        </>
      }
    >
      Profiles of the people we track appear here.
    </EmptyState>
  );
}

function useDebouncedQuery(search: ProfilesSearch, onSearch: (next: ProfilesSearch) => void) {
  const [text, setText] = useState(search.q ?? "");
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const q = text.trim() || undefined;
      if (q !== search.q) onSearch({ ...search, q });
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [text, search, onSearch]);
  return [text, setText] as const;
}

export function Roster({
  search,
  onSearch,
  onOpen,
}: {
  search: ProfilesSearch;
  onSearch: (next: ProfilesSearch) => void;
  onOpen: (id: string) => void;
}) {
  const roster = useQuery(rosterQuery);
  const session = useSession();
  const editor = session ? roleAtLeast(session.user.role, "editor") : false;
  const [text, setText] = useDebouncedQuery(search, onSearch);
  const [showNotTracked, setShowNotTracked] = useState(search.status === "not_tracked");
  const [rosterPanel, setRosterPanel] = useState<"import" | "export" | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const visibility = useColumnVisibility(ROSTER_VISIBILITY);
  const narrow = useNarrow();
  const hidden = useMemo(
    () => new Set([...visibility.hidden, ...(narrow ? NARROW_HIDDEN : [])]),
    [visibility.hidden, narrow],
  );
  const people = useMemo(() => roster.data?.people ?? [], [roster.data]);
  const options = useMemo(() => facetOptions(people), [people]);
  const view = filterRoster(people, search, showNotTracked);
  const shownRows = [...view.monitored, ...view.notTracked, ...view.retired];
  const reset = () => {
    setText("");
    onSearch({});
  };
  return (
    <div className="relative flex min-h-0 min-w-0 flex-1">
      <RosterSidebar
        open={filtersOpen}
        options={options}
        selected={view.selected}
        counts={view.counts}
        onToggle={(facet, key) => {
          onSearch(toggled(search, facet, key));
        }}
      />
      <section aria-labelledby="view-title" className="flex min-h-0 min-w-0 flex-1 flex-col">
        <RosterCounts counts={view.statusCounts} summary={roster.data?.summary} />
        <RosterToolbar
          filtersOpen={filtersOpen}
          onFilters={() => {
            setFiltersOpen(!filtersOpen);
          }}
          shown={shownRows.length}
          chips={rosterChips(search, options)}
          onChip={(c) => {
            onSearch(toggled(search, c.facet, c.key));
          }}
          onReset={reset}
          text={text}
          setText={setText}
          showNotTracked={showNotTracked}
          setShowNotTracked={setShowNotTracked}
          columns={visibility.toggles}
          editor={editor}
          onPanel={setRosterPanel}
          onNew={() => {
            onSearch({ ...search, new: "1" });
          }}
        />
        {roster.data && people.length === 0 ? (
          <RosterEmpty
            editor={editor}
            onNew={() => {
              onSearch({ ...search, new: "1" });
            }}
            onImport={() => {
              setRosterPanel("import");
            }}
          />
        ) : (
          <RosterGrid
            pending={roster.isPending}
            loaded={roster.data !== undefined}
            q={search.q ?? ""}
            rows={view}
            hidden={hidden}
            hiddenNotTracked={view.hiddenNotTracked.length}
            hiddenRetired={view.hiddenRetired.length}
            onOpen={onOpen}
            onReset={reset}
            onShowNotTracked={() => {
              setShowNotTracked(true);
            }}
            onShowRetired={() => {
              onSearch(toggled(search, "status", "retired"));
            }}
          />
        )}
      </section>
      {rosterPanel === "import" ? (
        <ImportPanel
          onClose={() => {
            setRosterPanel(null);
          }}
        />
      ) : null}
      {rosterPanel === "export" ? (
        <ExportDialog
          shownIds={shownRows.map((p) => p.id)}
          onClose={() => {
            setRosterPanel(null);
          }}
        />
      ) : null}
    </div>
  );
}
